import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { Assessment } from '../types';
import ResultsPdfExport from './ResultsPdfExport';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { CheckCircle, XCircle, HelpCircle, Eye, Shield, Lock, Users, FileText, Globe, IdCard, Settings, AlertTriangle, Database, Edit } from 'lucide-react';
import deiterateLogo from '@/assets/img/large_logo.svg';

// Icon mapping for each APP category
const appIcons = {
  'app-1': Eye,      // Transparent Management
  'app-2': Users,    // Anonymity
  'app-3': Database, // Collection
  'app-4': FileText, // Unsolicited
  'app-5': Globe,    // Notification
  'app-6': Shield,   // Use/Disclosure
  'app-7': AlertTriangle, // Direct Marketing
  'app-8': Settings, // Cross-border
  'app-9': IdCard,   // Gov IDs
  'app-10': Edit,    // Quality
  'app-11': Lock,    // Security
  'app-12': Database, // Access
  'app-13': Edit,    // Correction
};

// Horizontal bar chart component showing Yes/No/Unsure answer totals
const ComplianceBarChart: React.FC<{ responses: Record<string, any> }> = ({ responses }) => {
  // Count the Yes/No/Unsure answers
  let yesCount = 0;
  let noCount = 0;
  let unsureCount = 0;
  
  Object.values(responses).forEach(response => {
    switch (response.answer) {
      case 'Yes':
        yesCount++;
        break;
      case 'No':
        noCount++;
        break;
      case 'Unsure':
        unsureCount++;
        break;
    }
  });
  
  const chartData = [
    { name: 'Compliant', value: yesCount, color: '#4ade80' },
    { name: 'Not Compliant', value: noCount, color: '#f87171' },
    { name: 'Not Indicated', value: unsureCount, color: '#fb923c' },
  ];
  
  // Calculate dynamic maximum for full resolution scaling
  const maxValue = Math.max(yesCount, noCount, unsureCount, 1); // Ensure minimum of 1
  
  const chartConfig = {
    value: {
      label: 'Count',
      color: '#64748b',
    },
  } satisfies ChartConfig;
  
  return (
    <div className="h-[200px] w-full overflow-hidden">
      <ChartContainer config={chartConfig} className="w-full h-full">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
          barCategoryGap={4}
        >
          <XAxis type="number" domain={[0, maxValue]} tickCount={4} />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={100} 
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent 
                formatter={(value, name) => [`Count: ${value}`]}
                className="bg-card"
              />
            }
          />
          <Bar dataKey="value" barSize={32} radius={3}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};

const nameLookup = {
  'app-1': 'APP 1: Open & Transparent',
  'app-2': 'APP 2: Anonymity',
  'app-3': 'APP 3: Collection',
  'app-4': 'APP 4: Unsolicited',
  'app-5': 'APP 5: Notification',
  'app-6': 'APP 6: Use/Disclosure',
  'app-7': 'APP 7: Direct Marketing',
  'app-8': 'APP 8: Cross-border',
  'app-9': 'APP 9: Gov IDs',
  'app-10': 'APP 10: Quality',
  'app-11': 'APP 11: Security',
  'app-12': 'APP 12: Access',
  'app-13': 'APP 13: Correction',
};

// Exact recommendation text for each APP category
const appRecommendations = {
  'app-1': 'Have a clear, up-to-date privacy policy on your website that explains how you handle personal information, and simple steps for dealing with privacy questions or complaints.',
  'app-2': 'Where practical and legal, let people contact or buy from you without giving their real name (or by using a nickname), and tell them this option exists.',
  'app-3': 'Collect only the personal information you genuinely need for your business, in fair and lawful ways, and get consent before collecting sensitive information.',
  'app-4': 'If someone gives you personal information you didn\'t ask for, decide whether you were allowed to collect it, and if not, delete or de-identify it as soon as reasonably possible.',
  'app-5': 'When collecting personal information, tell people who you are, why you\'re collecting it, who you might share it with (including overseas), and how they can access or correct it.',
  'app-6': 'Only use or share personal information for the reason you collected it, unless: you have consent; the person would reasonably expect it; or the law clearly allows it.',
  'app-7': 'If you do direct marketing, only contact people when you have consent or a reasonable basis, and include a simple, free opt-out in every message and honour it promptly.',
  'app-8': 'Before sending personal information overseas check that they will protect it to Australian standards, or get the person\'s informed consent, and only disclose in line with APP 6.',
  'app-9': 'Avoid using government numbers (like Medicare or driver licence numbers) as your own customer IDs, and avoid sharing them, unless the law says you can.',
  'app-10': 'Take reasonable steps to keep personal information accurate, up-to-date, complete and relevant, and make it easy for people to update their details.',
  'app-11': 'Protect personal information from misuse, interference, loss, and unauthorised access or disclosure, and securely delete or de-identify it when no longer needed.',
  'app-12': 'Let people access their personal information on request in a prompt, straightforward and low-cost way, and explain how to do this in your privacy policy and notices.',
  'app-13': 'Correct personal information when someone asks, or when you discover it\'s wrong and, when appropriate, tell other organisations you\'ve shared it with about the correction.'
};

// Question card component showing individual APP compliance status
const QuestionCard: React.FC<{ categoryId: string; title: string; answer: string; question: string }> = ({ categoryId, title, answer, question }) => {
  const IconComponent = appIcons[categoryId as keyof typeof appIcons] || HelpCircle;
  
  // Determine status icon and color based on answer
  let StatusIcon = HelpCircle;
  let statusColor = 'text-orange-500';
  let borderColor = 'border-orange-200';
  let bgColor = 'bg-orange-50';
  
  if (answer === 'Yes') {
    StatusIcon = CheckCircle;
    statusColor = 'text-green-500';
    borderColor = 'border-green-200';
    bgColor = 'bg-green-50';
  } else if (answer === 'No') {
    StatusIcon = XCircle;
    statusColor = 'text-red-500';
    borderColor = 'border-red-200';
    bgColor = 'bg-red-50';
  }
  
  // Get the exact recommendation for this APP category
  const recommendation = appRecommendations[categoryId as keyof typeof appRecommendations] || 'Review this area and implement appropriate controls.';
  
  return (
    <Card className={`h-full ${borderColor} ${bgColor}`}>
      <CardContent className="py-2 px-3">
        <div className="flex items-center gap-4">
          <div className={`flex-shrink-0 ${statusColor} pl-2 pr-1`}>
            <IconComponent className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-2 leading-tight">{title}</h3>
            {(answer === 'No' || answer === 'Unsure') && (
              <p className="text-sm text-muted-foreground mb-2 leading-normal">{recommendation}</p>
            )}
            <div className="text-sm font-medium text-muted-foreground">
              Answer: {answer || 'Not answered'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface ResultsOverviewProps {
  assessment: Assessment | null;
}

const ResultsOverview: React.FC<ResultsOverviewProps> = ({ assessment }) => {
  const resultsRef = useRef<HTMLDivElement>(null);
  
  // Apply print-friendly attributes to chart elements when component mounts
  useEffect(() => {
    // hide id=questionnaire-title
    document.getElementById('questionnaire-title')?.classList.add('hidden');
    // Allow time for charts to render
    const timeoutId = setTimeout(() => {
      // Find all SVG elements and apply print-friendly attributes
      if (resultsRef.current) {
        const svgElements = resultsRef.current.querySelectorAll('svg *[fill]');
        svgElements.forEach(el => {
          // Store original fill for reference
          const fill = el.getAttribute('fill');
          if (fill) {
            el.setAttribute('data-original-fill', fill);
            
            // Apply data-attributes for print styles
            if (fill.includes('oklch')) {
              // Determine compliance level based on position/context
              const parent = el.parentElement;
              if (parent && parent.classList.contains('recharts-layer')) {
                // Try to determine which level this represents
                const index = Array.from(parent.children).indexOf(el as Element);
                if (index >= 0) {
                  el.setAttribute('data-level', String(index % 4));
                }
              }
            }
          }
        });
      }
    }, 1000); // Wait for charts to render
    
    return () => clearTimeout(timeoutId);
  }, [assessment]);
  
  // Get the responses from assessment - use the categories data if available, otherwise use maturityLevels
  const responses = assessment.categories?.reduce((acc, category) => {
    // Extract answer from the first response if available
    const answer = category.responses && category.responses.length > 0 
      ? category.responses[0].answer 
      : null;
    
    acc[category.categoryId] = {
      categoryId: category.categoryId,
      answer: answer,
      complianceLevel: category.maturityLevel
    };
    return acc;
  }, {} as Record<string, any>) || {};
  
  // Create question cards for each APP category
  const questionCards = Object.keys(nameLookup).map(categoryId => {
    const categoryData = responses[categoryId] || { answer: null };
    return {
      categoryId,
      title: nameLookup[categoryId as keyof typeof nameLookup],
      answer: categoryData.answer,
      question: '' // Question text not needed for the card display
    };
  });
  
  return (
    <ResultsPdfExport filename={`privacy-act-assessment-${assessment.questionnaireVersion.version}.pdf`}>
      <div ref={resultsRef} className="w-full max-w-full overflow-x-visible" style={{ WebkitOverflowScrolling: 'touch', minHeight: '-webkit-fill-available' }}>
        
        {/* Top 30% - Header Section with 3 columns */}
        <div className="grid grid-cols-3 gap-6 mb-6" style={{ minHeight: '25vh' }}>
          {/* Left column - Title */}
          <div className="flex items-center justify-start">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Privacy Act</h1>
              <h2 className="text-2xl font-semibold text-foreground">Compliance</h2>
              <h3 className="text-xl font-medium text-foreground">Snapshot</h3>
            </div>
          </div>
          
          {/* Center column - Bar Chart */}
          <div className="col-span-2 flex items-center justify-center">
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-center">Overall Privacy Act Health</CardTitle>
              </CardHeader>
              <CardContent>
                <ComplianceBarChart responses={responses} />
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom 70% - Question Cards Grid (3 columns, 5 rows) */}
        <div className="grid grid-cols-3 gap-6 mb-6" style={{ minHeight: '65vh' }}>
          {questionCards.map((card, index) => (
            <div key={card.categoryId}>
              <QuestionCard 
                categoryId={card.categoryId}
                title={card.title}
                answer={card.answer}
                question={card.question}
              />
            </div>
          ))}
          
          {/* Empty cells for grid positioning - 14th and 15th positions */}
          <div></div>
          <div className="flex items-end justify-end">
            <img src={deiterateLogo} alt="de.iterate" className="h-8 w-auto" />
          </div>
        </div>
        
      </div>
      <div className="w-full text-center py-2">
        <span className="text-xs font-medium">Questionnaire version: {assessment.questionnaireVersion.version}</span>
      </div>
    </ResultsPdfExport>
  );
};

export default ResultsOverview;
